/**
 *
 */
const GATSBY_PATH_PREFIX = __PATH_PREFIX__ || ""; // eslint-disable-line no-undef

// Transition directions
export const DIR_DOWN = "Down";
export const DIR_LEFT = "Left";
export const DIR_RIGHT = "Right";
export const DIR_UP = "Up";

// Page types
const T_ART = "article";
const T_CHAP = "chapter";
const T_HOME = "home";
const T_OTHER = "other";

/**
 * Run a series of checks against the input values. Returns a transition
 * direction.
 *
 * @param {*} current
 * @param {*} next
 */
const getTransition = (current, next) => {
  let direction = DIR_DOWN;

  // Start with some hard-coded path checks, regardless of path types
  const savedOverviewToDetail =
    current.path === "/saved/" && next.path === "/saved/article/";
  if (savedOverviewToDetail) return DIR_LEFT;

  const savedDetailToOverview =
    current.path === "/saved/article/" && next.path === "/saved/";
  if (savedDetailToOverview) return DIR_RIGHT;

  const filterOverviewToDetail =
    current.path === "/filter/" && next.path === "/filter/article/";
  if (filterOverviewToDetail) return DIR_LEFT;

  const filterDetailToOverview =
    current.path === "/filter/article/" && next.path === "/filter/";
  if (filterDetailToOverview) return DIR_RIGHT;

  const filterToSaved =
    current.path.startsWith("/filter/") && next.path.startsWith("/saved/");
  if (filterToSaved) return DIR_UP;

  const savedToFilter =
    current.path.startsWith("/saved/") && next.path.startsWith("/filter/");
  if (savedToFilter) return DIR_DOWN;

  // Continue with type and position based checks
  const chapterToArticle = current.type === T_CHAP && next.type === T_ART;
  const chapterToPrevArticle =
    chapterToArticle && current.index > next.chapterIndex;
  if (chapterToPrevArticle) return DIR_UP;
  if (chapterToArticle) return DIR_LEFT;

  const articleToChapter = current.type === T_ART && next.type === T_CHAP;
  const articleToNextChapter =
    articleToChapter && current.chapterIndex < next.index;
  if (articleToNextChapter) return DIR_DOWN;
  if (articleToChapter) return DIR_RIGHT;

  const chapterToChapter = current.type === T_CHAP && next.type === T_CHAP;
  const chapterToPrevChapter = chapterToChapter && current.index > next.index;
  if (chapterToPrevChapter) return DIR_UP;
  if (chapterToChapter) return DIR_DOWN; // If it's not up, it must be down

  const articleToArticle = current.type === T_ART && next.type === T_ART;
  const articleToPrevArticle = articleToArticle && current.index > next.index;
  if (articleToPrevArticle) return DIR_UP;
  if (articleToArticle) return DIR_DOWN; // If it's not up, it must be down

  const anywhereToHome = next.type === T_HOME;
  if (anywhereToHome) return DIR_UP;

  const anywhereToOther = next.type === T_OTHER;
  if (anywhereToOther) return DIR_DOWN;

  const otherToAnywhere = current.type === T_OTHER && next.type !== T_OTHER;
  if (otherToAnywhere) return DIR_UP;

  return direction;
};

/**
 * Given a path and arrays of sorted articlePaths and chapterPaths,
 * categorise which section of the site the path belongs to.
 *
 * For article or chapter paths, calculate the path's index (aka position)
 * within that category. e.g. the 10th article in the site.
 *
 * Article paths will also have their chapter index included, so we can detect
 * later whether to apply a different transition to article <--> chapter
 * path changes.
 *
 * articlePaths and chapterPaths shape:
 *
 * {
 *   path: {string},
 *   chapterId: {int} if a chapter - the chapter's id, if an article - the article's chapterId
 * }
 *
 * @param {string} path
 * @param {array} articlePaths an array of articlePath objects
 * @param {array} chapterPaths an array of chapterPath objects
 */
const getPathTypeAndPosition = (path, articlePaths, chapterPaths) => {
  // Get position of path in either article or chapter paths
  const articleIndex = articlePaths.findIndex(article => path === article.path);
  const chapterIndex = chapterPaths.findIndex(chapter => path === chapter.path);

  // path is an article?
  if (articleIndex > -1)
    return {
      type: T_ART,
      index: articleIndex,
      chapterIndex: chapterPaths.findIndex(
        // find the index of this article's chapter
        chapter => chapter.chapterId === articlePaths[articleIndex].chapterId
      ),
      path
    };

  // path is a chapter?
  if (chapterIndex > -1)
    return {
      type: T_CHAP,
      index: chapterIndex,
      path
    };

  if (path === "/") return { type: T_HOME, path }; // only one home page

  return { type: T_OTHER, path }; // default if we don't match anything else
};

/**
 * Run through a series of rules to work out direction the page transition
 * should be.
 *
 * @param {string} currentPath The current path
 * @param {string} nextPath The next path
 * @param {array} articlePaths An array of objects describing paths
 * @param {array} chapterPaths An array of objects describing paths
 */
// prettier-ignore
const transitionDirection = (currentPath, nextPath, articlePaths, chapterPaths) => {
  // Account for url prefixes
  const unprefixedCurrentPath = currentPath.startsWith(GATSBY_PATH_PREFIX)
  ? currentPath.replace(GATSBY_PATH_PREFIX, "")
  : currentPath;

  // Account for url prefixes
  const unprefixedNextPath = nextPath.startsWith(GATSBY_PATH_PREFIX)
  ? nextPath.replace(GATSBY_PATH_PREFIX, "")
  : nextPath;

  const current = getPathTypeAndPosition(unprefixedCurrentPath, articlePaths, chapterPaths);
  const next = getPathTypeAndPosition(unprefixedNextPath, articlePaths, chapterPaths);

  return {
    isArticleTransition: current.type === T_ART && next.type === T_ART,
    direction: getTransition(current, next)
  }
};
export default transitionDirection;



// WEBPACK FOOTER //
// ./src/utils/transitionDirection.js